import angular from "angular";
import { SettingsService, UiHelpers } from "../../services";
import { CalcRowScriptConfig, InputEnumOption, RowTypeDefinition, HelpersRoot, Employment, Employments, Calculations, Calculation } from "@salaxy/core";
/**
 * Controller for the CalcRowFormula component that provides formula editing capabilities for calculation rows.
 * Used by the CalcRowFormula component to manage formula editing functionality.
 * @implements {angular.IController}
 */
export class CalcRowFormulaController implements angular.IController {
  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["SettingsService", "UiHelpers", "Employments", "Calculations"];
  /** Currently edited row */
  public row: RowTypeDefinition;

  /** Available properties for scripting */
  public scriptNames: InputEnumOption[] = [
    {
      value: "price",
      text: "Hinta",
    },
    {
      value: "count",
      text: "Määrä",
    },
    {
      value: "message",
      text: "Rivin teksti",
    },
    {
      value: "unit",
      text: "Yksikkö",
    },
    {
      value: "sourceId",
      text: "ID lähdejärjestelmässä",
    },
    {
      value: "accounting.dimensions",
      text: "Kustannuspaikat",
    },
    {
      value: "accounting.vatPercent",
      text: "ALV %",
    },
    {
      value: "accounting.vatEntries",
      text: "ALV rivit",
    },
    {
      value: "period",
      text: "Palkkakausi",
    },
    {
      value: "period.start",
      text: "Palkkaukauden alku",
    },
    {
      value: "period.end",
      text: "Palkkaukauden loppu",
    },
    {
      value: "period.daysCount",
      text: "Palkkaukauden työpäivien määrä",
    },
    {
      value: "period.days",
      text: "Palkkaukauden työpäivät",
    },
    {
      value: "data",
      text: "Rivin data"
    },
    {
      value: "data.kind",
      text: "Rivin alalaji"
    },
    {
      value: "calculation",
      text: "Koko laskelman muokkaus"
    }
  ];

  public selectedEmployment: Employment;
  public selectedCalc: Calculation;

  /** Selected script name for addScript */
  public selectedScriptName: string;

  public evalResult: string;

  /**
   * Creates a new EmbeddedUiController
   * @ignore
   */
  constructor(private settingsService: SettingsService, private uiHelpers: UiHelpers, private employments: Employments, private calculations: Calculations) { }

  /** Gets the description for a script name */
  public getDescription(scriptName: string) {
    return this.scriptNames.find(n => n.value === scriptName)?.text;
  }

  /** Returns true if the selected script name has already been added. */
  public get selectedScriptAdded(): boolean {
    // TODO: add kind support later: name + kind as unique key
    return !!this.row.config?.scripts?.some(script => script.name === this.selectedScriptName);
  }

  /**
   * Adds a new script expression to the row.
   * @param {string} scriptName - The name of the script to add. selectedScriptName is used if not provided.
   */
  public addScript(scriptName?: string) {
    scriptName = scriptName || this.selectedScriptName;
    this.row.config.scripts ??= [];
    if (this.row.config.scripts.some(script => script.name === scriptName)) {
      return;
    }
    this.row.config.scripts.push({ name: scriptName, script: "" });
  }

  /** Name of the script that is currently edited in full-dialog editor. */
  public editingScript?: CalcRowScriptConfig;

  /**
   * Selects a script for editing in full-dialog editor.
   * @param {string} script - The script object to edit.
   * Undefined for closing the editor.
   */
  public editScript(script?: CalcRowScriptConfig) {
    this.editingScript = script;
    this.evaluateEditingScript();
  }

  /**
   * Removes a script from the row.
   * @param {string} script - The script to remove.
   */
  public removeScript(script: CalcRowScriptConfig) {
    const index = this.row.config.scripts?.indexOf(script);
    if (index >= 0) {
      this.row.config.scripts.splice(index, 1);
    }
  }

  /**
   * Determines if the currently edited script can be evaluated.
   * A script is evaluatable when all required dependencies are available:
   * - A selected employment is set
   * - A selected calculation is set
   * - A script is currently being edited
   * @returns {boolean} True if the script can be evaluated, false otherwise
   */
  public isScriptEvaluatable(): boolean {
    return !!(this.selectedEmployment && this.selectedCalc && this.editingScript);
  }

  /**
   * Evaluates the currently edited script using a wrapped eval pattern.
   * The script can refer to $data (which includes sxy and settings).
   * @returns {*} The result of the evaluated script.
   */
  public evaluateEditingScript(): any {
    this.evalResult = "";
    if (!this.isScriptEvaluatable()) {
      return;
    }
    /* const settings = this.settingsService.current;
    globalThis.settings = settings;
    const fn = Function(`return (${this.editingScript.script})`);
    const result = fn.bind({sxy: new HelpersRoot()})(); */
    //TODO: Instantiate row from this.row
    //Check rowsService or something
    //globalThis.row = {rowType: this.row.config.type};
    globalThis.sxy = new HelpersRoot();
    globalThis.settings = angular.copy(this.settingsService.current as any);
    globalThis.employment = angular.copy(this.selectedEmployment);
    globalThis.calculation = angular.copy(this.selectedCalc);
    globalThis.row = {};
    if (this.editingScript.name === "calculation") {
      const fn = new Function(`return (${this.editingScript.script})`);
      const returnedCalculation = fn();
      this.evalResult = JSON.stringify(fn(), null, 2);
      if (returnedCalculation) {
        angular.copy(returnedCalculation, this.selectedCalc);
      }
    }
    else
    {
      const fn = new Function(`return (${this.editingScript.script})`);
      this.evalResult = JSON.stringify(fn(), null, 2);
    }
  }
  /**
   * Opens a dialog to select an existing calculation.
   * @returns {Promise<void>} A promise that resolves when the dialog is closed.
   */
  public addExistingCalc() {
    return this.uiHelpers.openEditDialog(
      "salaxy-components/modals/calc/CalcList.html",
      [],
      {
        title: "SALAXY.UI_Terms.select",
        category: "draft",
        options:  {$filter: "status eq 'draft'"},
        okButtonText: "SALAXY.UI_Terms.ok",
        cancelButtonText: "SALAXY.UI_Terms.cancel",
      }).then((result) => {
      if (result.action === "ok" && result.item.length == 1) {
       return this.calculations.getSingle(result.item[0].id).then((calc) => {
        this.selectedCalc = calc;
        console.log(this.selectedCalc);
        return this.employments.getSingle(calc.worker.employmentId).then((employment) => {
          this.selectedEmployment = employment;
          console.log(this.selectedEmployment);
        });
       });
      } else if (result.action === "ok" && result.item.length > 1) {
        this.uiHelpers.showAlert("Valitse vain yksi laskelma");
      }
    });
  }
}
